// AI task structuring client — auto-routes between environments:
//   - Tauri Desktop / iOS Native: invoke `ai_structure_task` (→ Rust → Hermes proxy)
//   - PWA on flow.gaten-saiyo.com / *.pages.dev: POST /api/ai/structure (Cloudflare Pages Function)
//   - Vite dev (localhost): direct call to Hermes proxy on localhost:8645 if reachable

import { api, type AgentProvider, type AiStructuredTask, type AiStructureContext, type ClassifyResult, type FirstMove, type Task } from "./tauri";

const PWA_PROVIDER_LABELS: Record<AgentProvider, string> = { codex: "AI 実行", claude: "AI 整理" };
const NATIVE_PROVIDER_LABELS: Record<AgentProvider, string> = { codex: "Codex", claude: "Claude" };

export function providerLabel(p: AgentProvider): string {
  return isTauriNative() ? NATIVE_PROVIDER_LABELS[p] : PWA_PROVIDER_LABELS[p];
}

export type { FirstMove };

export type StructuredTask = AiStructuredTask;
export type StructureContext = AiStructureContext;

export function isTauriNative(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as Record<string, unknown>;
  return (
    "__TAURI_INTERNALS__" in w ||
    "__TAURI_METADATA__" in w ||
    "__TAURI__" in w
  );
}

function isProductionPwa(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h.endsWith("gaten-saiyo.com") || h.endsWith("pages.dev");
}

export async function structureTask(
  text: string,
  context?: StructureContext
): Promise<StructuredTask> {
  const ctx: StructureContext = context ?? { now: new Date().toISOString() };

  // 1. Tauri (Desktop / iOS): Rust → Hermes proxy
  if (isTauriNative() && api.aiStructureTask) {
    return api.aiStructureTask(text, ctx);
  }

  // 2. PWA on production: Cloudflare Pages Function
  if (isProductionPwa()) {
    const res = await fetch("/api/ai/structure", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, context: ctx }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`AI structure ${res.status}: ${t.slice(0, 200)}`);
    }
    return (await res.json()) as StructuredTask;
  }

  // 3. Vite dev / unknown environment: try local Hermes proxy directly
  const res = await fetch("http://localhost:8645/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer hermes-proxy",
    },
    body: JSON.stringify({
      model: "grok-4-fast",
      messages: [
        { role: "system", content: "You return only JSON: {title, description, dueDate, priority, confidence}" },
        { role: "user", content: `現在時刻: ${ctx.now ?? new Date().toISOString()}\n入力: 「${text}」\n上記から1件のタスクをJSONで返してください。` },
      ],
      response_format: { type: "json_object" },
      temperature: 0.2,
      max_tokens: 400,
    }),
  });
  if (!res.ok) throw new Error(`Hermes proxy ${res.status}`);
  const data = await res.json();
  const content = data?.choices?.[0]?.message?.content ?? "";
  const parsed = JSON.parse(content);
  return {
    title: String(parsed.title ?? text).slice(0, 80),
    description: String(parsed.description ?? ""),
    dueDate: typeof parsed.dueDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(parsed.dueDate)
      ? parsed.dueDate
      : null,
    priority:
      parsed.priority === "high" || parsed.priority === "low" ? parsed.priority : "medium",
    confidence: typeof parsed.confidence === "number" ? parsed.confidence : 0.5,
  };
}

export async function classifyTasks(tasks: Task[]): Promise<ClassifyResult> {
  // Tauri (Desktop): Rust → Ollama local
  if (isTauriNative() && api.aiClassifyTasks) {
    return api.aiClassifyTasks(tasks);
  }

  // PWA on production: Cloudflare Pages Function → NVIDIA NIM by default
  if (isProductionPwa()) {
    if (tasks.length === 0) return { today: [], thisWeek: [], later: [] };
    const payload = tasks.map((t) => ({
      id: t.id,
      title: t.title,
      priority: t.priority,
      dueDate: t.dueDate ?? null,
    }));
    const res = await fetch("/api/ai/classify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tasks: payload }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`AI classify ${res.status}: ${t.slice(0, 200)}`);
    }
    return (await res.json()) as ClassifyResult;
  }

  throw new Error("ローカルAI(Ollama)はデスクトップアプリでのみ使えます");
}

export async function suggestFirstMove(tasks: Task[]): Promise<FirstMove> {
  // Tauri (Desktop): Rust → Ollama local
  if (isTauriNative() && api.aiSuggestFirstMove) {
    return api.aiSuggestFirstMove(tasks);
  }

  // PWA on production: Cloudflare Pages Function → NVIDIA NIM by default
  if (isProductionPwa()) {
    if (tasks.length === 0) return { taskId: null, message: "" };
    const payload = tasks.map((t) => ({
      id: t.id,
      title: t.title,
      priority: t.priority,
      dueDate: t.dueDate ?? null,
    }));
    const res = await fetch("/api/ai/firstmove", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tasks: payload }),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`AI firstmove ${res.status}: ${t.slice(0, 200)}`);
    }
    return (await res.json()) as FirstMove;
  }

  throw new Error("ローカルAI(Ollama)はデスクトップアプリでのみ使えます");
}

export async function warmup(): Promise<void> {
  if (isTauriNative() && api.aiWarmup) {
    await api.aiWarmup().catch(() => { /* Ollama未起動でも無視 */ });
  }
}

export function isAiAvailable(): boolean {
  if (typeof window === "undefined") return false;
  // Tauri Native (Desktop / iOS): Rust が Hermes proxy 経由でサブスク利用
  if (isTauriNative()) return true;
  // PWA on production: Pages Function → server-side AI provider
  if (isProductionPwa()) return true;
  // dev: optimistically true; structureTask() will surface failure if proxy is down
  return true;
}
